import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../services/AuthContext';
import { Check, Zap, Sparkles, Shield, Send, X, Mail } from 'lucide-react';
import API_URL from '../config/API_URL';
import emailjs from 'emailjs-com';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('free');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Access code states
  const [accessCodeRequired, setAccessCodeRequired] = useState(false);
  const [accessCode, setAccessCode] = useState('');
  const [verifiedAccessCode, setVerifiedAccessCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [accessCodeError, setAccessCodeError] = useState('');
  
  // Request code states
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestEmail, setRequestEmail] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [requestStatus, setRequestStatus] = useState({ type: '', message: '' });
  
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Initialize EmailJS
    emailjs.init('gHDv2j1boFBdQTfm3');
    
    // Check if access codes are enabled
    const checkAccessCodeStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/access-code-status`);
        const data = await response.json();
        setAccessCodeRequired(data.enabled);
      } catch (err) {
        console.error('Failed to check access code status:', err);
      }
    };

    checkAccessCodeStatus();
  }, []);

  const handleAccessCodeVerify = async () => {
    setAccessCodeError('');
    
    if (accessCode.length !== 6) {
      setAccessCodeError('Access code must be 6 characters');
      return;
    }

    setIsVerifying(true);

    try {
      const response = await fetch(`${API_URL}/api/access-codes/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: accessCode.toUpperCase() })
      });

      const data = await response.json();

      if (data.valid) {
        setVerifiedAccessCode(accessCode.toUpperCase());
        setAccessCodeError('');
      } else {
        setAccessCodeError(data.message || 'Invalid access code');
      }
    } catch (err) {
      setAccessCodeError('Failed to verify code. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setIsRequestingCode(true);
    setRequestStatus({ type: '', message: '' });

    const emailData = {
      email: requestEmail,
      subject: 'LOOP - Access Code Request',
      message: `
      LOOP ACCESS CODE REQUEST
      ========================
      
      Email: ${requestEmail}
      Reason: ${requestReason || 'Not provided'}
      
      Requested at: ${new Date().toLocaleString()}
      User Agent: ${navigator.userAgent}
      ========================
      `.trim()
    };

    try {
      await emailjs.send('get_email', 'template_646tc2u', emailData);
      
      setRequestStatus({
        type: 'success',
        message: 'Request submitted successfully! We\'ll review your request and get back to you soon.'
      });
      
      // Reset form
      setRequestEmail('');
      setRequestReason('');
      
      // Close modal after 3 seconds
      setTimeout(() => {
        setShowRequestModal(false);
        setRequestStatus({ type: '', message: '' });
      }, 3000);
    } catch (error) {
      console.error('EmailJS Error:', error);
      setRequestStatus({
        type: 'error',
        message: 'Failed to submit request. Please try again or contact us directly.'
      });
    } finally {
      setIsRequestingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const registrationData = {
        name,
        email,
        password
      };

      // Include access code if required
      if (accessCodeRequired && verifiedAccessCode) {
        registrationData.access_code = verifiedAccessCode;
      }

      if (selectedPlan === 'premium') {
        localStorage.setItem('pendingPremiumUpgrade', 'true');
      }
      
      const result = await register(registrationData.name, registrationData.email, registrationData.password, registrationData.access_code);
      
      if (result.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
      localStorage.removeItem('pendingPremiumUpgrade');
      // Reset access code if registration fails
      if (accessCodeRequired) {
        setVerifiedAccessCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  const planStyles = {
    container: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    planCard: {
      border: '2px solid #e0e7ef',
      borderRadius: '12px',
      padding: '1.25rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
      position: 'relative',
    },
    planCardSelected: {
      borderColor: '#667eea',
      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
    },
    planHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '0.75rem',
    },
    planName: {
      fontSize: '1.1rem',
      fontWeight: 700,
      color: '#1e293b',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    planPrice: {
      fontSize: '1.5rem',
      fontWeight: 800,
      color: '#667eea',
    },
    planPeriod: {
      fontSize: '0.75rem',
      color: '#64748b',
      marginTop: '0.25rem',
    },
    planFeatures: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
      fontSize: '0.85rem',
      color: '#475569',
    },
    planFeature: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginBottom: '0.5rem',
    },
    checkIcon: {
      color: '#10b981',
      flexShrink: 0,
    },
    badge: {
      display: 'inline-block',
      background: '#dcfce7',
      color: '#166534',
      padding: '0.25rem 0.5rem',
      borderRadius: '6px',
      fontSize: '0.7rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      marginTop: '0.5rem',
    },
    premiumBadge: {
      background: '#dbeafe',
      color: '#1e40af',
    },
  };

  const formDisabled = accessCodeRequired && !verifiedAccessCode;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: isMobile ? '15px' : '20px',
      overflow: 'auto',
    }}>
      {/* Request Code Modal */}
      {showRequestModal && (
        <>
          <style>{`
            @keyframes slideIn {
              from {
                opacity: 0;
                transform: translate(-50%, -48%) scale(0.95);
              }
              to {
                opacity: 1;
                transform: translate(-50%, -50%) scale(1);
              }
            }
            
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
          
          {/* Modal Backdrop */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(5px)',
              zIndex: 10000,
              animation: 'fadeIn 0.2s ease-out'
            }}
            onClick={() => setShowRequestModal(false)}
          />
          
          {/* Modal Content */}
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            borderRadius: '24px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            zIndex: 10001,
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflowY: 'auto',
            animation: 'slideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '32px 32px 24px',
              borderBottom: '1px solid #e0e7ef'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Mail size={24} color="white" />
                </div>
                <div>
                  <h3 style={{
                    margin: '0 0 4px 0',
                    fontSize: '24px',
                    fontWeight: '700',
                    color: '#1a1a1a'
                  }}>Request Access Code</h3>
                  <p style={{
                    margin: 0,
                    color: '#64748b',
                    fontSize: '14px'
                  }}>We'll review your request</p>
                </div>
              </div>
              <button
                style={{
                  background: '#f1f5f9',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  color: '#64748b'
                }}
                onClick={() => setShowRequestModal(false)}
                onMouseEnter={(e) => {
                  e.target.style.background = '#e2e8f0';
                  e.target.style.color = '#1a1a1a';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#f1f5f9';
                  e.target.style.color = '#64748b';
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleRequestCode} style={{ padding: '24px 32px 32px' }}>
              {/* Status Message */}
              {requestStatus.message && (
                <div style={{
                  marginBottom: '24px',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  border: `1px solid ${requestStatus.type === 'success' ? '#10b981' : '#ef4444'}`,
                  background: requestStatus.type === 'success' ? '#dcfce7' : '#fee2e2',
                  color: requestStatus.type === 'success' ? '#166534' : '#991b1b',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {requestStatus.message}
                </div>
              )}

              {/* Email */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1e293b'
                }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  value={requestEmail}
                  onChange={(e) => setRequestEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  required
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: 'white',
                    border: '2px solid #e0e7ef',
                    borderRadius: '12px',
                    color: '#1a1a1a',
                    fontSize: '16px',
                    transition: 'all 0.3s ease',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e7ef'}
                />
              </div>

              {/* Reason */}
              <div style={{ marginBottom: '32px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1e293b'
                }}>
                  Reason (Optional)
                </label>
                <textarea
                  value={requestReason}
                  onChange={(e) => setRequestReason(e.target.value)}
                  placeholder="Tell us why you'd like to join Loop..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '16px',
                    background: 'white',
                    border: '2px solid #e0e7ef',
                    borderRadius: '12px',
                    color: '#1a1a1a',
                    fontSize: '16px',
                    transition: 'all 0.3s ease',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    minHeight: '100px',
                    lineHeight: '1.5',
                    outline: 'none',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e0e7ef'}
                />
              </div>

              {/* Buttons */}
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  type="button"
                  onClick={() => setShowRequestModal(false)}
                  style={{
                    padding: '14px 24px',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: 'white',
                    color: '#64748b',
                    border: '2px solid #e0e7ef'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#cbd5e1';
                    e.target.style.color = '#475569';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#e0e7ef';
                    e.target.style.color = '#64748b';
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!requestEmail || isRequestingCode}
                  style={{
                    padding: '14px 32px',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: (requestEmail && !isRequestingCode) ? 'pointer' : 'not-allowed',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: (!requestEmail || isRequestingCode)
                      ? '#cbd5e1'
                      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    opacity: (!requestEmail || isRequestingCode) ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (requestEmail && !isRequestingCode) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 20px rgba(102, 126, 234, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  {isRequestingCode && (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255, 255, 255, 0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                  )}
                  {!isRequestingCode && <Send size={16} />}
                  {isRequestingCode ? 'Sending...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      <div style={{
        width: '100%',
        maxWidth: isMobile ? '500px' : '1200px',
        display: isMobile ? 'flex' : 'grid',
        flexDirection: isMobile ? 'column' : undefined,
        gridTemplateColumns: isMobile ? undefined : '1fr 1fr',
        gap: isMobile ? '30px' : '60px',
        alignItems: 'center',
      }}>
        {/* Branding Side */}
        <div style={{ 
          color: 'white',
          textAlign: isMobile ? 'center' : 'left',
        }}>
          <img 
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="Loop Logo" 
            style={{
              width: isMobile ? '180px' : '220px',
              height: isMobile ? '150px' : '180px',
              objectFit: 'contain',
              filter: 'brightness(0) invert(1) drop-shadow(0 10px 30px rgba(0,0,0,0.3))',
            }}
          />
          <p style={{ 
            fontSize: isMobile ? '16px' : '20px', 
            marginBottom: isMobile ? '20px' : '30px', 
            opacity: 0.9,
            margin: 0,
            marginTop: isMobile ? '12px' : '20px',
          }}>
            Stay Connected, Stay in the Loop
          </p>
          
          <div style={{
            height: '3px',
            width: '100%',
            background: 'rgba(255, 255, 255, 0.3)',
            marginBottom: isMobile ? '20px' : '30px',
            marginTop: isMobile ? '20px' : '30px',
          }} />
          <h2 style={{ 
            fontSize: isMobile ? '20px' : '24px', 
            fontWeight: '600', 
            marginBottom: '10px', 
            margin: 0,
          }}>
            Smart Networking Assistant
          </h2>
          <p style={{ 
            fontSize: isMobile ? '14px' : '16px', 
            opacity: 0.8, 
            lineHeight: 1.6, 
            margin: 0, 
            marginTop: '10px',
          }}>
            Keep your professional network active with intelligent follow-ups and relationship management
          </p>

          {!isMobile && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '15px', 
              marginTop: '30px',
            }}>
              <div style={{
                display: 'flex',
                gap: '15px',
                padding: '20px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
              }}>
                <div style={{ fontSize: '24px' }}>👥</div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', marginBottom: '5px' }}>
                    Network Management
                  </h4>
                  <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>
                    Organize and track your professional connections with ease
                  </p>
                </div>
              </div>
              <div style={{
                display: 'flex',
                gap: '15px',
                padding: '20px',
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '12px',
              }}>
                <div style={{ fontSize: '24px' }}>✉️</div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', marginBottom: '5px' }}>
                    AI-Powered Messages
                  </h4>
                  <p style={{ margin: 0, fontSize: '14px', opacity: 0.8 }}>
                    Generate personalized follow-ups and outreach messages instantly
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Form Side */}
        <div style={{
          background: 'white',
          borderRadius: isMobile ? '20px' : '24px',
          padding: isMobile ? '30px 25px' : '50px',
          boxShadow: '0 25px 80px rgba(0, 0, 0, 0.3)',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative',
        }}>
          {/* Access Code Required Overlay */}
          {formDisabled && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(3px)',
              borderRadius: isMobile ? '20px' : '24px',
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
            }}>
              <div style={{
                textAlign: 'center',
                maxWidth: '400px',
                width: '100%',
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  marginBottom: '20px',
                  boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)',
                }}>
                  <Shield size={40} color="white" />
                </div>
                <h3 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '12px',
                  margin: 0,
                }}>
                  Access Code Required
                </h3>
                <p style={{
                  fontSize: '15px',
                  color: '#666',
                  marginBottom: '25px',
                  margin: 0,
                  marginTop: '12px',
                  lineHeight: 1.6,
                }}>
                  This registration is invite-only. Enter your 6-character access code to continue.
                </p>

                {accessCodeError && (
                  <div style={{
                    padding: '14px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    backgroundColor: '#fee',
                    color: '#c00',
                    border: '1px solid #fcc',
                    marginBottom: '20px',
                    textAlign: 'center',
                  }}>
                    {accessCodeError}
                  </div>
                )}

                <div style={{ marginBottom: '20px' }}>
                  <input
                    type="text"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                    placeholder="Enter 6-character code"
                    maxLength={6}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '16px',
                      border: '2px solid #e0e7ef',
                      borderRadius: '12px',
                      fontSize: '18px',
                      fontWeight: '600',
                      textAlign: 'center',
                      letterSpacing: '4px',
                      outline: 'none',
                      textTransform: 'uppercase',
                      fontFamily: 'monospace',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>

                <button
                  onClick={handleAccessCodeVerify}
                  disabled={isVerifying || accessCode.length !== 6}
                  style={{
                    width: '100%',
                    padding: '16px 40px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '700',
                    cursor: (isVerifying || accessCode.length !== 6) ? 'not-allowed' : 'pointer',
                    opacity: (isVerifying || accessCode.length !== 6) ? 0.6 : 1,
                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                    marginBottom: '16px',
                  }}
                >
                  {isVerifying ? 'Verifying...' : 'Verify Access Code'}
                </button>

                {/* Request Code Button */}
                <button
                  onClick={() => setShowRequestModal(true)}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'white',
                    color: '#667eea',
                    border: '2px solid #667eea',
                    borderRadius: '12px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#667eea';
                    e.target.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'white';
                    e.target.style.color = '#667eea';
                  }}
                >
                  <Mail size={16} />
                  Request Access Code
                </button>
              </div>
            </div>
          )}

          <div style={{ 
            marginBottom: isMobile ? '28px' : '35px', 
            textAlign: 'center',
            opacity: formDisabled ? 0.3 : 1,
          }}>
            <h3 style={{ 
              fontSize: isMobile ? '26px' : '32px', 
              fontWeight: '700', 
              color: '#667eea', 
              marginBottom: '10px',
              margin: 0,
            }}>
              Create Account
            </h3>
            <p style={{ 
              fontSize: isMobile ? '14px' : '15px', 
              color: '#5a6c7d',
              margin: 0,
              marginTop: '10px',
            }}>
              Join Loop and elevate your networking game
            </p>

            {/* Show verified access code badge */}
            {verifiedAccessCode && (
              <div style={{
                marginTop: '15px',
                padding: '10px 20px',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                border: '1px solid #10b981',
                borderRadius: '12px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                fontWeight: '600',
                color: '#10b981',
              }}>
                <Check size={16} />
                Access Code Verified
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: isMobile ? '18px' : '22px',
            opacity: formDisabled ? 0.3 : 1,
            pointerEvents: formDisabled ? 'none' : 'auto',
          }}>
            {error && (
              <div style={{
                padding: isMobile ? '14px' : '16px',
                borderRadius: '12px',
                fontSize: '14px',
                backgroundColor: '#fee',
                color: '#c00',
                border: '1px solid #fcc',
              }}>
                {error}
              </div>
            )}

            {/* Plan Selection */}
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#667eea', 
                marginBottom: '12px',
              }}>
                Choose Your Plan
              </label>
              <div style={planStyles.container}>
                {/* Free Plan */}
                <div
                  style={{
                    ...planStyles.planCard,
                    ...(selectedPlan === 'free' ? planStyles.planCardSelected : {}),
                  }}
                  onClick={() => setSelectedPlan('free')}
                >
                  <div style={planStyles.planHeader}>
                    <div>
                      <div style={planStyles.planName}>
                        <Zap size={18} color="#667eea" />
                        Free
                      </div>
                      <div style={planStyles.planPrice}>$0</div>
                      <div style={planStyles.planPeriod}>forever</div>
                    </div>
                  </div>
                  <ul style={planStyles.planFeatures}>
                    <li style={planStyles.planFeature}>
                      <Check size={14} style={planStyles.checkIcon} />
                      Contact management
                    </li>
                    <li style={planStyles.planFeature}>
                      <Check size={14} style={planStyles.checkIcon} />
                      AI message generation
                    </li>
                    <li style={planStyles.planFeature}>
                      <Check size={14} style={planStyles.checkIcon} />
                      Basic features
                    </li>
                  </ul>
                  <span style={planStyles.badge}>Perfect to start</span>
                </div>

                {/* Premium Plan */}
                <div
                  style={{
                    ...planStyles.planCard,
                    ...(selectedPlan === 'premium' ? planStyles.planCardSelected : {}),
                  }}
                  onClick={() => setSelectedPlan('premium')}
                >
                  <div style={planStyles.planHeader}>
                    <div>
                      <div style={planStyles.planName}>
                        <Sparkles size={18} color="#667eea" />
                        Premium
                      </div>
                      <div style={planStyles.planPrice}>$15</div>
                      <div style={planStyles.planPeriod}>per month</div>
                    </div>
                  </div>
                  <ul style={planStyles.planFeatures}>
                    <li style={planStyles.planFeature}>
                      <Check size={14} style={planStyles.checkIcon} />
                      Everything in Free
                    </li>
                    <li style={planStyles.planFeature}>
                      <Check size={14} style={planStyles.checkIcon} />
                      Unlimited transcription
                    </li>
                    <li style={planStyles.planFeature}>
                      <Check size={14} style={planStyles.checkIcon} />
                      News schedulers
                    </li>
                    <li style={planStyles.planFeature}>
                      <Check size={14} style={planStyles.checkIcon} />
                      Topic briefings
                    </li>
                  </ul>
                  <span style={{ ...planStyles.badge, ...planStyles.premiumBadge }}>
                    Best Value
                  </span>
                </div>
              </div>
              {selectedPlan === 'premium' && (
                <div style={{
                  fontSize: '0.85rem',
                  color: '#64748b',
                  marginTop: '0.5rem',
                  textAlign: 'center',
                }}>
                  You'll be redirected to secure payment after registration
                </div>
              )}
            </div>
            
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#667eea', 
                marginBottom: '8px',
              }}>
                Full Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your full name"
                required
                autoFocus
                style={{
                  width: '100%',
                  padding: isMobile ? '14px' : '16px',
                  border: '2px solid #e0e7ef',
                  borderRadius: '12px',
                  fontSize: '15px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#667eea', 
                marginBottom: '8px',
              }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                style={{
                  width: '100%',
                  padding: isMobile ? '14px' : '16px',
                  border: '2px solid #e0e7ef',
                  borderRadius: '12px',
                  fontSize: '15px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ 
              display: isMobile ? 'flex' : 'grid',
              flexDirection: isMobile ? 'column' : undefined,
              gridTemplateColumns: isMobile ? undefined : '1fr 1fr', 
              gap: isMobile ? '18px' : '15px',
            }}>
              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#667eea', 
                  marginBottom: '8px',
                }}>
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create password"
                  required
                  style={{
                    width: '100%',
                    padding: isMobile ? '14px' : '16px',
                    border: '2px solid #e0e7ef',
                    borderRadius: '12px',
                    fontSize: '15px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: '#667eea', 
                  marginBottom: '8px',
                }}>
                  Confirm Password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  required
                  style={{
                    width: '100%',
                    padding: isMobile ? '14px' : '16px',
                    border: '2px solid #e0e7ef',
                    borderRadius: '12px',
                    fontSize: '15px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <button 
              type="submit" 
              disabled={loading || formDisabled}
              style={{
                width: '100%',
                padding: isMobile ? '16px' : '18px',
                background: selectedPlan === 'premium' 
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: isMobile ? '16px' : '17px',
                fontWeight: '700',
                cursor: (loading || formDisabled) ? 'not-allowed' : 'pointer',
                opacity: (loading || formDisabled) ? 0.7 : 1,
                marginTop: '8px',
              }}
            >
              {loading ? 'Creating account...' : 
                selectedPlan === 'premium' ? 'Create Account & Subscribe' : 'Create Free Account'
              }
            </button>
          </form>

          <div style={{ 
            marginTop: isMobile ? '22px' : '25px', 
            textAlign: 'center', 
            fontSize: isMobile ? '14px' : '15px', 
            color: '#5a6c7d',
            opacity: formDisabled ? 0.3 : 1,
          }}>
            <p style={{ margin: 0 }}>
              Already have an account?{' '}
              <span 
                onClick={() => navigate('/login')} 
                style={{ color: '#667eea', fontWeight: '700', cursor: 'pointer' }}
              >
                Sign In
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;